import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  ChartLineUp, Gauge, Function, Sliders, PencilSimpleLine, ClockCounterClockwise,
  Buildings, UsersThree, ListChecks, SignOut, Sun, Moon, CaretDown, Gear,
} from "@phosphor-icons/react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { formatPeriod } from "@/lib/helpers";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ErrorBoundary from "@/components/ErrorBoundary";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, roles: ["admin", "capturista", "ejecutivo"] },
  { to: "/historicos", label: "Históricos", icon: ChartLineUp, roles: ["admin", "capturista", "ejecutivo"] },
  { to: "/captura", label: "Captura Manual", icon: PencilSimpleLine, roles: ["admin", "capturista"] },
  { to: "/kpis", label: "Indicadores (KPI)", icon: Function, roles: ["admin", "capturista", "ejecutivo"] },
  { to: "/variables", label: "Variables", icon: Sliders, roles: ["admin", "capturista", "ejecutivo"] },
  { to: "/ajustes", label: "Ajustes y configuraciones", icon: Gear, roles: ["admin"] },
  { to: "/usuarios", label: "Usuarios", icon: UsersThree, roles: ["admin"] },
  { to: "/auditoria", label: "Bitácora", icon: ListChecks, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const {
    companies, periods,
    selectedCompanyIds, toggleCompany, selectCompany,
    consolidated, setConsolidated,
    period, setPeriod,
  } = useApp();

  const visibleNav = NAV.filter((n) => n.roles.includes(user?.role));

  const companyLabel =
    selectedCompanyIds.length === 0 ? "Sin empresas"
    : consolidated
      ? (selectedCompanyIds.length === companies.length
          ? "Todas las plantas (consolidado)"
          : `${selectedCompanyIds.length} ${selectedCompanyIds.length === 1 ? "planta" : "plantas"} (consolidado)`)
      : (companies.find((c) => c.id === selectedCompanyIds[0])?.name || "Planta");

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] md:flex" data-testid="sidebar">
        <div className="flex items-center gap-2.5 px-6 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ChartLineUp weight="duotone" size={22} aria-hidden="true" />
          </div>
          <div className="leading-tight">
            <p className="font-heading text-base font-bold tracking-tight text-primary">ZAE KPI</p>
            <p className="text-xs text-muted-foreground">Gestión de Indicadores</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-2">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-primary/10 hover:text-primary"
                  }`
                }
              >
                <Icon weight="duotone" size={20} aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-border p-3">
          <div className="rounded-lg bg-primary/5 px-3 py-2.5 text-xs">
            <p className="font-medium text-foreground">Fase 1 · Captura manual</p>
            <p className="mt-0.5 text-muted-foreground">Preparado para Odoo 18 SH</p>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-10 flex flex-wrap items-center gap-3 border-b border-border bg-card/70 px-4 py-3 backdrop-blur-xl md:px-6">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-9 justify-start gap-2" data-testid="company-selector">
                  <Buildings weight="duotone" size={16} className="text-muted-foreground" aria-hidden="true" />
                  <span className="max-w-[260px] truncate">{companyLabel}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <div className="flex items-center justify-between px-2 py-1.5" data-testid="consolidate-toggle-row">
                  <div className="leading-tight">
                    <p className="text-sm font-medium">Consolidar plantas</p>
                    <p className="text-xs text-muted-foreground">Sumar todas las plantas</p>
                  </div>
                  <Switch
                    checked={consolidated}
                    onCheckedChange={setConsolidated}
                    data-testid="consolidate-switch"
                    disabled={companies.length < 2}
                  />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {consolidated ? "Plantas incluidas en el consolidado" : "Selecciona una planta"}
                </DropdownMenuLabel>
                {companies.map((c) => (
                  consolidated ? (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={selectedCompanyIds.includes(c.id)}
                      onCheckedChange={() => toggleCompany(c.id)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`company-option-${c.code}`}
                    >
                      {c.name}
                    </DropdownMenuCheckboxItem>
                  ) : (
                    <DropdownMenuItem
                      key={c.id}
                      onSelect={() => selectCompany(c.id)}
                      className={selectedCompanyIds[0] === c.id ? "bg-accent font-medium" : ""}
                      data-testid={`company-option-${c.code}`}
                    >
                      {c.name}
                    </DropdownMenuItem>
                  )
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Select value={period || ""} onValueChange={setPeriod}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="period-selector">
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`period-option-${p}`}>
                    {formatPeriod(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle-button"
            aria-label="Cambiar tema"
          >
            {theme === "dark" ? <Sun weight="duotone" size={20} /> : <Moon weight="duotone" size={20} />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent" data-testid="user-menu-trigger">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {user?.name?.slice(0, 2).toUpperCase()}
                </div>
                <div className="hidden text-left leading-tight sm:block">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
                </div>
                <CaretDown size={14} className="text-muted-foreground" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>
                <p>{user?.email}</p>
                <p className="text-xs font-normal text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                data-testid="logout-button"
              >
                <SignOut size={16} className="mr-2" aria-hidden="true" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
